import {
	SlipSheetOption,
	LayoutTransform,
	hasSlipSheet,
	Layout_t,
} from 'blackbox';
import { useState, useEffect } from 'react';
import {
	getChangeWrapperNumber,
	wrapChangeEventNumber,
} from '../../shared/shared';
import {
	AutoStackOptionProps,
	AutoStackProps,
	AutoStackParams,
} from './stackTypes';
import { createDefaultAutoStackParams } from './stackUtils';

function AutoStackOption({ name, children }: AutoStackOptionProps) {
	return (
		<div className="full-width-minus-2pad flex-col align-start double-margin-top double-margin-bottom">
			<span className="bold-font margin-bottom">{name}</span>
			{children}
		</div>
	);
}

export default function AutoStack({
	allPallets,
	palletIndex,
	initialLayerCount,
	onAutoStackParamsChange,
	initialAutoStackParams,
	onKeyboardTrigger,
	onSelectTrigger,
}: AutoStackProps) {
	// State
	const [autoStackParams, setAutoStackParams] = useState<AutoStackParams>(
		initialAutoStackParams ??
			createDefaultAutoStackParams(palletIndex, initialLayerCount, allPallets)
	);

	// Layer‐count change handler
	const layerCountChange = getChangeWrapperNumber((newCount: number) => {
		if (newCount < 1) return;

		const updated = { ...autoStackParams, layerCount: newCount };

		if (updated.slipSheet.config !== SlipSheetOption.None) {
			const currentIds = Object.values(updated.slipSheet.ids);
			if (updated.slipSheet.config !== SlipSheetOption.Custom) {
				// all layers get the same sheet
				if (currentIds.length > 0) {
					applySlipSheetToAll(updated, currentIds[0]);
				}
			} else {
				trimSlipSheetIds(updated);
			}
		}

		setAutoStackParams(updated);
	});

	// Open keyboard input for layer count
	const openLayerCountInput = () => {
		if (!onKeyboardTrigger) return;
		onKeyboardTrigger(
			'Enter number of layers in stack',
			String(autoStackParams.layerCount),
			layerCountChange.FromString
		);
	};

	// Sync back out
	useEffect(() => {
		onAutoStackParamsChange(autoStackParams);
	}, [autoStackParams]);

	// If parent changes layer count
	useEffect(() => {
		if (
			initialLayerCount === autoStackParams.layerCount ||
			initialLayerCount === 0
		)
			return;
		layerCountChange.Direct(initialLayerCount);
	}, [initialLayerCount]);

	// If pallet index changes, reset layout index
	useEffect(() => {
		if (palletIndex === autoStackParams.palletIndex) return;
		setAutoStackParams({
			...autoStackParams,
			palletIndex,
			layoutIndex: 0,
		});
	}, [palletIndex]);

	// Layout index setter
	const handleLayoutIndexChange = (layoutIndex: number) => {
		setAutoStackParams({ ...autoStackParams, layoutIndex });
	};

	// Even/odd transform setter
	const handleLayerTransformChange =
		(isEven: boolean) => (transformIndex: number) => {
			const updated = { ...autoStackParams };
			if (isEven) {
				updated.evenTransform = transformIndex as LayoutTransform.TransformType;
			} else {
				updated.oddTransform = transformIndex as LayoutTransform.TransformType;
			}
			setAutoStackParams(updated);
		};

	// Slip-sheet config setter
	const handleSlipSheetConfigChange = (config: SlipSheetOption) => {
		const updated: AutoStackParams = {
			...autoStackParams,
			slipSheet: {
				...autoStackParams.slipSheet,
				exclude: {},
				config,
				ids: config === SlipSheetOption.None ? {} : autoStackParams.slipSheet.ids,
			},
		};
		setAutoStackParams(updated);
	};

	// Helpers for slip-sheet IDs
	const applySlipSheetToAll = (
		paramsRef: AutoStackParams,
		slipSheetId: string
	) => {
		const ids: Record<number, string> = {};
		for (let i = 0; i <= paramsRef.layerCount; i++) {
			if (hasSlipSheet(i, paramsRef.layerCount, paramsRef.slipSheet)) {
				ids[i] = slipSheetId;
			}
		}
		paramsRef.slipSheet.ids = ids;
	};

	const trimSlipSheetIds = (paramsRef: AutoStackParams) => {
		const { slipSheet } = paramsRef;
		Object.keys(slipSheet.ids).forEach(key => {
			const idx = Number(key);
			if (idx >= paramsRef.layerCount) {
				delete slipSheet.ids[idx];
				if (slipSheet.exclude) delete slipSheet.exclude[idx];
			}
		});
	};

	// Select‐trigger wrappers
	const triggerLayoutSelection = (e: React.SyntheticEvent) => {
		if (!onSelectTrigger) return;
		e.preventDefault();
		onSelectTrigger(
			'Select Layout',
			allPallets[palletIndex].Layouts.map(l => l.name),
			handleLayoutIndexChange,
			autoStackParams.layoutIndex
		);
	};

	const triggerEvenBehaviorSelection = (e: React.SyntheticEvent) => {
		if (!onSelectTrigger) return;
		e.preventDefault();
		onSelectTrigger(
			'Select Even-Numbered Layer Behavior',
			LayoutTransform.TransformTypeNames,
			handleLayerTransformChange(true),
			autoStackParams.evenTransform
		);
	};

	const triggerOddBehaviorSelection = (e: React.SyntheticEvent) => {
		if (!onSelectTrigger) return;
		e.preventDefault();
		onSelectTrigger(
			'Select Odd-Numbered Layer Behavior',
			LayoutTransform.TransformTypeNames,
			handleLayerTransformChange(false),
			autoStackParams.oddTransform
		);
	};

	return (
		<div className="full-width-minus-2pad flex-col align-center color-blue auto-height">
			<AutoStackOption name="Select Layout:">
				<select
					className="width-100"
					value={autoStackParams.layoutIndex}
					onChange={wrapChangeEventNumber(handleLayoutIndexChange)}
					onMouseDown={triggerLayoutSelection}
				>
					{allPallets[palletIndex].Layouts.map(({ name }, idx) => (
						<option key={idx} value={idx}>
							{name}
						</option>
					))}
				</select>
			</AutoStackOption>

			<AutoStackOption name="Number Of Layers:">
				<input
					className="width-100"
					type="number"
					value={autoStackParams.layerCount}
					onChange={layerCountChange.Event}
					onClick={openLayerCountInput}
				/>
			</AutoStackOption>

			<AutoStackOption name="Odd-Numbered Layer Behavior:">
				<select
					className="width-100"
					value={autoStackParams.oddTransform}
					onChange={wrapChangeEventNumber(handleLayerTransformChange(false))}
					onMouseDown={triggerOddBehaviorSelection}
				>
					{LayoutTransform.TransformTypeNames.map((label, idx) => (
						<option key={idx} value={idx}>
							{label}
						</option>
					))}
				</select>
			</AutoStackOption>

			<AutoStackOption name="Even-Numbered Layer Behavior:">
				<select
					className="width-100"
					value={autoStackParams.evenTransform}
					onChange={wrapChangeEventNumber(handleLayerTransformChange(true))}
					onMouseDown={triggerEvenBehaviorSelection}
				>
					{LayoutTransform.TransformTypeNames.map((label, idx) => (
						<option key={idx} value={idx}>
							{label}
						</option>
					))}
				</select>
			</AutoStackOption>
		</div>
	);
}
